import { formatPrice, formatQuantity } from "@common/utils/string";
import { hasMetabaseAI, offerManagedMetabaseAI } from "@stores/config/utils";
import { HOSTING_FEATURE_METABASE_AI } from "@stores/subscriptions/constants";
import { getInstanceMetabaseMajorVersion } from "@utils/metabot";
import { ManageCard } from "./ManageCard";
import { prepaidEstimate, subscribedUnits, tieredTotal } from "../utils";
import {
  IAddOnContext,
  IUpsertContext,
  IPrepaidAddOnDescriptor,
} from "../types";

const requests = (n: number): string => `${formatQuantity(n)} requests`;

/** Legacy tiered Metabot. */
const metabaseAiTiered = {
  productType: "metabase-ai-tiered",
  kind: "prepaid",
  title: "Metabot",

  // managed wins when both are subscribed: harbormaster's ai_transition
  // cutover assumes it is the visible one
  isVisible: ({
    purchase,
    config,
    subscribed,
    addOn,
    subscribedByType,
    isCloudHosted,
  }) => {
    if (!isCloudHosted || subscribedByType["metabase-ai-managed"]) {
      return false;
    }

    if (subscribed) {
      return true;
    }

    // `list-addons` drops the tiered row once it stops being active
    if (
      !addOn?.product_tiers?.length ||
      !hasMetabaseAI(config) ||
      offerManagedMetabaseAI(config)
    ) {
      return false;
    }

    const majorVersion = getInstanceMetabaseMajorVersion(
      purchase.hosted_instance?.mb_version,
    );

    return majorVersion === null || majorVersion < 60;
  },

  getUsage: ({ purchase, subscribed }: IAddOnContext) => {
    if (!subscribed) {
      return undefined;
    }

    const quota = purchase.quota?.[HOSTING_FEATURE_METABASE_AI]?.queries;

    return {
      hasQuota: !!quota,
      usage: quota?.usage ?? 0,
      includedUnits: subscribed.prepaid_units,
    };
  },

  getEstimateRowItem: (ctx, units) => ({
    detail: requests(units),
    price: prepaidEstimate(ctx, units),
  }),

  getUpsertAddOns: ({ config }: IUpsertContext, units: number) =>
    units && hasMetabaseAI(config)
      ? [
          {
            product_type: "metabase-ai-tiered",
            prepaid_units: units,
          },
        ]
      : [],

  getBillingRowItem: (ctx) => ({ detail: requests(subscribedUnits(ctx)) }),
  getCheckoutRowItem: ({ addOn }, units) => ({
    detail: requests(units),
    price: addOn.default_price_per_unit * units,
  }),

  getPurchaseLineItem: (ctx) => {
    const { subscribed } = ctx;

    if (!subscribed) {
      return undefined;
    }

    const usage = ctx.getUsage();
    const total = tieredTotal(ctx);

    return {
      detail: `${usage?.hasQuota ? `${usage.usage}/` : ""}${formatQuantity(subscribed.prepaid_units)} requests`,
      price: total === null ? undefined : formatPrice(total),
    };
  },

  ManageCard,
} satisfies IPrepaidAddOnDescriptor;

export default metabaseAiTiered;
